import { useEffect } from 'react'
import { useCookies } from 'react-cookie'

const IS_POP = 'true' as const
const COOKIE_KEYS = {
  IS_POP: 'isPop',
  SCROLL_Y: 'scrollY',
} as const

export const useIsPopCookie = () => {
  const [, setCookie] = useCookies([COOKIE_KEYS.IS_POP])

  const setIsPopCookie = () => {
    setCookie(COOKIE_KEYS.IS_POP, IS_POP, { path: '/', sameSite: 'strict' })
  }

  return { setIsPopCookie }
}

/**
 * 뒤로가기 감지시 쿠키에 isPop=true 저장
 *
 * @example
 * const DetailPage = () => {
 *  useDetectBackNavigation()
 * }
 */
export const useDetectBackNavigation = () => {
  const { setIsPopCookie } = useIsPopCookie()

  useEffect(() => {
    const handlePopState = () => {
      setIsPopCookie()
    }

    window.addEventListener('popstate', handlePopState)
    return () => {
      window.removeEventListener('popstate', handlePopState)
    }
  }, [])
}

/**
 * isPop cookie가 true인 경우 스크롤 위치 복원
 *
 * @example
 * const ListPage = () => {
 *  const { saveScroll } = useSaveScroll()
 *  useRestoreScrollOnBack()
 *
 *  return (
 *     <div>
 *       {cards.map((card) => (
 *         <Card key={card.id} onClick={() => {
 *           saveScroll()
 *           router.push(card.url)
 *         }} />
 *       ))}
 *     </div>
 *   )
 * }
 */
export const useRestoreScrollOnBack = ({ onBack }: { onBack?: () => void } = {}) => {
  const [cookies, , removeCookie] = useCookies([COOKIE_KEYS.IS_POP, COOKIE_KEYS.SCROLL_Y])

  useEffect(() => {
    const isBackNav = cookies[COOKIE_KEYS.IS_POP] === IS_POP
    if (isBackNav) {
      // saveScroll()에서 저장한 스크롤 위치 복원
      const scrollY = cookies[COOKIE_KEYS.SCROLL_Y]
      if (scrollY) {
        window.scrollTo(0, parseInt(scrollY))
        removeCookie(COOKIE_KEYS.SCROLL_Y, { path: '/' })
      }
      removeCookie(COOKIE_KEYS.IS_POP, { path: '/' })
      onBack?.()
    }
  }, [cookies, removeCookie, onBack])
}

/**
 * `useRestoreScrollOnBack` 훅에서 복원할 스크롤 위치 저장
 *
 * @example
 * const CardList = () => {
 *   const handleClickCard = () => {
 *    // 스크롤 저장
 *    saveScroll()
 *    router.push(URL)
 *   }
 * }
 */
export const useSaveScroll = () => {
  const [, setCookie] = useCookies([COOKIE_KEYS.SCROLL_Y])

  const saveScroll = () => {
    if (typeof window === 'undefined') return
    const y = Math.round(window.scrollY)
    setCookie(COOKIE_KEYS.SCROLL_Y, y.toString(), { path: '/', sameSite: 'strict' })
  }

  return { saveScroll }
}
